import { Form as FormType, moonbase } from "@/lib/moonbase";
import { useEffect, useState } from "react";
import z from "zod";

type ZodFormSchema = z.ZodObject<z.ZodRawShape, z.UnknownKeysParam, z.ZodTypeAny>;

type LoadingFormSchema = {
  status: "loading";
};

type LoadedFormSchema = {
  status: "success";
  formSchema: ZodFormSchema;
  formName: string;
  formDescription: string | null;
};

type NotFoundFormSchema = {
  status: "not_found";
};

type ErrorFormSchema = {
  status: "error";
  error: Error;
};

type FormSchemaLoaderResult =
  | LoadedFormSchema
  | LoadingFormSchema
  | NotFoundFormSchema
  | ErrorFormSchema;

const supportedFieldTypes = [
  { type: "uri", subType: "url" },
  { type: "text", subType: "single_line" },
  { type: "text", subType: "multi_line" },
  { type: "geo" },
  { type: "boolean" },
  { type: "email" },
  { type: "telephone_number" },
  { type: "date" },
  { type: "datetime" },
  { type: "choice" },
] as const;

function buildZodSchema(form: FormType) {
  const zodSchema = z.object({} as Record<(typeof form.schema.fields)[number]["key"], z.ZodType>);

  for (const field of form.schema.fields) {
    const isSupported = supportedFieldTypes.some((supported) => {
      return (
        supported.type === field.type &&
        ("subType" in supported
          ? "subType" in field && field.subType !== null
            ? supported.subType === field.subType
            : false
          : "subType" in field && field.subType !== null
            ? false
            : true)
      );
    });

    if (!isSupported) {
      console.warn(
        `unsupported field type: ${field.type}${
          "subType" in field && field.subType !== null ? `/${field.subType}` : ""
        }`,
      );
      continue;
    }

    // this format is what the JS submission API expects
    const fieldName = `field_${field.id}`;

    switch (field.type) {
      case "boolean":
        zodSchema.shape[fieldName] = z.boolean();
        break;
      case "email":
        zodSchema.shape[fieldName] = z.string().email();
        break;
      case "uri": {
        zodSchema.shape[fieldName] = z.preprocess((val) => {
          if (typeof val !== "string") {
            return val;
          }
          return moonbase.normalize.normalizeUrl(val) ?? val;
        }, z.string().url());
        break;
      }
      case "telephone_number":
        zodSchema.shape[fieldName] = z.string().regex(/^\+[1-9]\d{1,14}$/, {
          message: "Please enter a valid telephone number in E.164 format (e.g. +15554444333).",
        });
        break;
      case "date":
        zodSchema.shape[fieldName] = z.string().date();
        break;
      case "datetime":
        zodSchema.shape[fieldName] = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/);
        break;
      case "choice": {
        const optionIds = field.options.map((option) => option.id);
        if (optionIds.length === 0) {
          throw new Error(`Field ${fieldName} has no options`);
        }

        // TODO we do not know the options type statically
        if (field.cardinality === "many") {
          zodSchema.shape[fieldName] = z.array(z.enum(optionIds as [string, ...string[]]));
        } else {
          zodSchema.shape[fieldName] = z.enum(optionIds as [string, ...string[]]);
        }
        break;
      }
      default:
        zodSchema.shape[fieldName] = z.string();
    }

    let isRequired = false;
    const validations = form.schema.validations[field.id];
    if (validations !== undefined && Array.isArray(validations)) {
      for (const validation of validations) {
        if (validation === "required") {
          isRequired = true;
          break;
        }
      }
    }
    if (!isRequired) {
      zodSchema.shape[fieldName] = z.preprocess(
        (val) => (val === "" ? undefined : val),
        zodSchema.shape[fieldName].optional(),
      );
    }

    zodSchema.shape[fieldName] = zodSchema.shape[fieldName].meta(field);
  }

  return zodSchema;
}

function useFormSchemaLoader(formId: string | undefined): FormSchemaLoaderResult {
  const [result, setResult] = useState<FormSchemaLoaderResult>({ status: "loading" });

  useEffect(() => {
    const load = async () => {
      try {
        if (!formId) {
          setResult({ status: "not_found" });
          return;
        }

        const form = await moonbase.forms.getForm(formId);
        const zodSchema = buildZodSchema(form);
        setResult({
          status: "success",
          formSchema: zodSchema,
          formName: form.schema.name,
          formDescription: form.schema.description,
        });
      } catch (err: unknown) {
        const error = err as Error & { status?: number };

        if (error.status === 404) {
          setResult({ status: "not_found" });
        } else {
          setResult({ status: "error", error });
        }
      }
    };

    load();
  }, [formId]);

  return result;
}

export { useFormSchemaLoader, type LoadedFormSchema, type LoadingFormSchema, type ZodFormSchema };
